import React, { useState } from "react";
import { TitleBlock, Field, Input, SelectInput, Note, WarnNote, Actions, PrimaryBtn, TextBtn } from "../../../../auth/v2/pieces";
import { US_STATE_ABBRS } from "../../../../ForAthletes/v2/onboarding/onboardingPieces";
import { isRequestMatch, matchMeta, possibleMatchesOf, requestedLine, teamFromMatch } from "../../../../ForAthletes/v2/onboarding/steps/TeamSteps";
import { TeamPickRow } from "../coachOnboardingPieces";
import { errorMessage, requestReady, teamInitials, usesUsStates } from "../coachOnboardingModel";
import { displayTeamName } from "../../../../../utils/teamNames";

// 206px from 640px up. On phones the city (or region) field takes the room
// the 126px state picker leaves (Figma 2466:19197), so the row fits any
// column from 272px (a 320px screen) up.
const CITY_INPUT = "!w-[206px] max-sm:!w-auto max-sm:min-w-0 max-sm:flex-1";

// A college team's SportsRadar league and the sport the Team step keys it by.
const SR_SPORT = { ncaamb: "mbb", ncaawb: "wbb" };

// A possible match as the Team step's picked team (the same identity as its
// row in the Team list): a partner team, a college team (its own source, so
// it is never filed as a Cerebro team) or a program squad; null when it has
// no roster to bind to.
const matchTeam = (match) => {
  const t = teamFromMatch(match);
  const partner = !!t.cacheTeamId;
  const league = partner ? t.cacheLeague : t.catalogueKey;
  if (!league) return null;
  const base = {
    id: partner ? t.cacheTeamId : t.id,
    name: t.name,
    leagueName: t.leagueName,
    location: [t.city, t.state].filter(Boolean).join(", "),
  };
  if (partner) return { ...base, source: "cache_teams", cacheLeague: league };
  if (t.teamSource === "sportsradar") return SR_SPORT[league] ? { ...base, source: "sportsradar", srSport: SR_SPORT[league] } : null;
  return { ...base, source: "cerebro", cerebroLeague: league, leagueKey: t.leagueKey || undefined };
};

// The API already has teams under the typed name (the request answered 409
// possible_matches): pick one, or add the team anyway. Rows read by the
// name people see (displayName) and the server's listing label, a team
// others asked for by how many asked; never the row's source.
const PossibleMatches = ({ teamName, matches, busy, error, onPick, onAddAnyway, onRename }) => (
  <>
    <TitleBlock
      eyebrow="Team · not listed"
      title="Is it one of these?"
      sub={`We found teams named like ${teamName}. Pick yours to continue with it, or add your team if none of these is it.`}
    />
    <div className="flex flex-col gap-[10px]">
      {matches.map((m, i) => {
        const team = matchTeam(m);
        const name = displayTeamName(m) || m.name || m.teamName || "";
        let meta = "No roster on file yet";
        if (team) meta = matchMeta(m);
        else if (isRequestMatch(m)) meta = `${requestedLine(m)} · No roster on file yet`;
        return (
          <TeamPickRow
            key={`${m.cacheTeamId || m.teamId || m.id}|${m.leagueKey || m.league || i}`}
            initials={teamInitials(name)}
            name={name}
            meta={meta}
            disabled={!team || busy}
            onClick={() => onPick(team)}
          />
        );
      })}
    </div>
    {error && <Note tone="critical">{error}</Note>}
    <Actions>
      <PrimaryBtn loading={busy} onClick={onAddAnyway}>
        None of these, add my team
      </PrimaryBtn>
      <TextBtn onClick={onRename}>Change the team name</TextBtn>
    </Actions>
  </>
);

// 08a · Can't find your team. The request answers possible matches first;
// otherwise it records the team for an admin and submits the coach for
// verification with it.
const TeamRequestStep = ({ filters, request, onRequestChange, onSubmit, onPickMatch, onBackToSearch }) => {
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const form = { teamName: "", city: "", state: "", ...request };
  const set = (patch) => onRequestChange({ ...form, ...patch });
  const us = usesUsStates(filters);

  const send = async (confirmed) => {
    setBusy(true);
    setError("");
    try {
      await onSubmit(form, confirmed);
    } catch (e) {
      const found = possibleMatchesOf(e);
      if (found?.length && !confirmed) setMatches(found);
      else setError(errorMessage(e, "We could not send your team. Try again."));
      setBusy(false);
    }
  };

  if (matches) {
    return (
      <PossibleMatches
        teamName={form.teamName.trim()}
        matches={matches}
        busy={busy}
        error={error}
        onPick={onPickMatch}
        onAddAnyway={() => send(true)}
        onRename={() => {
          setMatches(null);
          setError("");
        }}
      />
    );
  }
  return (
    <>
      <TitleBlock
        eyebrow="Team · not listed"
        title="Can't find your team?"
        sub={
          <>
            Make sure you've looked under <span className="font-semibold">{filters.sport?.name}</span> and the <span className="font-semibold">{filters.tier?.name}</span> tier. Still no
            luck? Tell us your team's name and we'll add it, then you can finish setting up and complete your profile now.
          </>
        }
      />
      <Field label="Your team's name">
        <Input aria-label="Your team's name" placeholder="e.g. Oakland Soldiers 17U" value={form.teamName} onChange={(e) => set({ teamName: e.target.value })} />
      </Field>
      <Field label={us ? "City and state" : "City and region"}>
        <div className="flex items-start gap-[10px]">
          <Input className={CITY_INPUT} aria-label="City" placeholder="Oakland" value={form.city} onChange={(e) => set({ city: e.target.value })} />
          {us ? (
            // Required, so the unpicked placeholder reads grey as in Figma.
            <SelectInput className="w-[126px] [&_select:invalid]:text-bm-placeholder" required aria-label="State" value={form.state} onChange={(e) => set({ state: e.target.value })}>
              <option value="" disabled hidden>
                CA
              </option>
              {US_STATE_ABBRS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </SelectInput>
          ) : (
            <Input className={CITY_INPUT} aria-label="Region" placeholder="Region" value={form.state} onChange={(e) => set({ state: e.target.value })} />
          )}
        </div>
      </Field>
      <WarnNote badge="Pending review">An admin will review and add your team so you and your teammates can select it later.</WarnNote>
      {error && <Note tone="critical">{error}</Note>}
      <Actions>
        <PrimaryBtn disabled={!requestReady(form)} loading={busy} onClick={() => send(false)}>
          Continue, add my team
        </PrimaryBtn>
        <TextBtn onClick={onBackToSearch}>Go back and search</TextBtn>
      </Actions>
    </>
  );
};

export default TeamRequestStep;
